import { Inject, Injectable } from '@angular/core';
import { TBAppEnvironmentVars } from '@monorepo/types';

const RootCauseMappingEndpoints = {
  RCA_UPLOAD: 'RCA_UPLOAD',
  RCA_TEMPLATE: 'RCA_TEMPLATE',
  RCA_DOWNLOAD_ERROR_FILE: 'RCA_DOWNLOAD_ERROR_FILE',
  RCA_MAPPINGS: 'RCA_MAPPINGS',
  RCA_MAPPINGS_CRUD: 'RCA_MAPPINGS_CRUD',
  RCA_CONFIG: 'RCA_CONFIG',
  RCA_VALIDATE: 'RCA_VALIDATE',
  RCA_HIERARCHICAL: 'RCA_HIERARCHICAL',
  RCA_CATEGORIES: 'RCA_CATEGORIES',
  RCA_ROOT_CAUSES_BY_CATEGORY: 'RCA_ROOT_CAUSES_BY_CATEGORY',
  RCA_CORRECTIVE_ACTIONS_BY_ROOT_CAUSE: 'RCA_CORRECTIVE_ACTIONS_BY_ROOT_CAUSE',
  RCA_PREVENTIVE_ACTIONS_BY_ROOT_CAUSE: 'RCA_PREVENTIVE_ACTIONS_BY_ROOT_CAUSE',
};

type TRootCauseMappingEndpoints = typeof RootCauseMappingEndpoints;
type TRootCauseMappingEndpointsTypes = keyof TRootCauseMappingEndpoints;
type TEnvVariables = TRootCauseMappingEndpoints;

@Injectable({
  providedIn: 'root',
})
export class RootCauseMappingEnvService {
  private env_vars!: TEnvVariables;
  private protocol = 'https';
  private service_host = '';
  private core_services = '';

  constructor(@Inject('environment') environment: TBAppEnvironmentVars) {
    if (environment.protocol !== null) {
      this.protocol = environment.protocol;
    }
    if (environment.service_host !== null) {
      this.service_host = environment.service_host;
    }
    if (environment.core_services !== null) {
      this.core_services = environment.core_services;
    }
    this.registerVars();
  }

  registerVars() {
    const HOST = this.protocol + this.service_host + this.core_services;

    const baseURL = '/api/v1/rca';

    this.env_vars = {
      RCA_UPLOAD: `${HOST}${baseURL}/upload`,
      RCA_TEMPLATE: `${HOST}${baseURL}/template`,
      RCA_DOWNLOAD_ERROR_FILE: `${HOST}${baseURL}`,
      RCA_MAPPINGS: `${HOST}${baseURL}/mappings`, // append /:factoryId
      RCA_MAPPINGS_CRUD: `${HOST}${baseURL}/mappings/crud`,
      RCA_CONFIG: `${HOST}${baseURL}/config`, // append /:factoryId
      RCA_VALIDATE: `${HOST}${baseURL}/validate`, // append /:factoryId
      RCA_HIERARCHICAL: `${HOST}${baseURL}/hierarchical`, // append /:factoryId
      RCA_CATEGORIES: `${HOST}${baseURL}/categories`,
      RCA_ROOT_CAUSES_BY_CATEGORY: `${HOST}${baseURL}/rootcauses`, // append /:factoryId/:category
      RCA_CORRECTIVE_ACTIONS_BY_ROOT_CAUSE: `${HOST}${baseURL}/actions`, // append /:rootCauseId
      RCA_PREVENTIVE_ACTIONS_BY_ROOT_CAUSE: `${HOST}${baseURL}/preventiveactions`, // append /:rootCauseId
    };
  }

  getEnvVar(key: TRootCauseMappingEndpointsTypes): string {
    return this.env_vars[key];
  }

  // Helper methods to build URLs with dynamic parameters
  getMappingsUrl(factoryId: string): string {
    return `${this.getEnvVar('RCA_MAPPINGS')}/${factoryId}`;
  }

  getConfigUrl(factoryId: string): string {
    return `${this.getEnvVar('RCA_CONFIG')}/${factoryId}`;
  }

  getValidateUrl(factoryId: string): string {
    return `${this.getEnvVar('RCA_VALIDATE')}/${factoryId}`;
  }

  getHierarchicalUrl(factoryId: string): string {
    return `${this.getEnvVar('RCA_HIERARCHICAL')}/${factoryId}`;
  }

  getRootCausesByCategoryUrl(factoryId: string, category: string): string {
    return `${this.getEnvVar('RCA_ROOT_CAUSES_BY_CATEGORY')}/${factoryId}/${category}`;
  }

  getCorrectiveActionsByRootCauseUrl(rootCauseId: number): string {
    return `${this.getEnvVar('RCA_CORRECTIVE_ACTIONS_BY_ROOT_CAUSE')}/${rootCauseId}`;
  }

  getPreventiveActionsByRootCauseUrl(rootCauseId: number): string {
    return `${this.getEnvVar('RCA_PREVENTIVE_ACTIONS_BY_ROOT_CAUSE')}/${rootCauseId}`;
  }
}
